'use client';

import { ProductProps } from '@/types/Product';
import { Flex, Grid, Icon, Text, useBreakpointValue } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { IconType } from 'react-icons';
import { LuArrowRight } from 'react-icons/lu';
import Link from 'next/link';
import ProductCard from '../ProductCard';
import ArrowButton from '../ArrowButton';
import { pushDataLayerEvent, toEcommerceItem } from '@/utils/dataLayer';

type ProductsGridProps = {
  products: ProductProps[];
  title?: string;
  IconReact: IconType;
  /** Ligação opcional para uma página com a listagem completa (ex: "Ver tudo"). */
  viewAllHref?: string;
  /** Etiqueta opcional mostrada junto ao título (ex: tag/nome de uma campanha). */
  tagLabel?: string;
  tagColor?: string;
};

const ProductsGrid = ({
  products,
  title,
  IconReact,
  viewAllHref,
  tagLabel,
  tagColor,
}: ProductsGridProps) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [mouseHover, setMouseHover] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const showArrows = isMobile || mouseHover;

  useEffect(() => {
    const items = products
      .filter((p): p is ProductProps => !!p && !p.coming_soon)
      .map((p) => toEcommerceItem(p));

    if (items.length === 0) return;

    pushDataLayerEvent('view_item_list', {
      ecommerce: {
        item_list_name: title ?? 'Lista de produtos',
        items,
      },
    });
  }, [products, title]);

  const scrollLeft = () => {
    if (gridRef.current) {
      if (gridRef.current.scrollLeft === 0) {
        gridRef.current.scrollTo({
          left: gridRef.current.scrollWidth,
          behavior: 'smooth',
        });
      } else {
        gridRef.current.scrollBy({ left: -280, behavior: 'smooth' });
      }
    }
  };

  const scrollRight = () => {
    if (gridRef.current) {
      if (
        gridRef.current.scrollLeft + gridRef.current.clientWidth >=
        gridRef.current.scrollWidth
      ) {
        gridRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        gridRef.current.scrollBy({ left: 280, behavior: 'smooth' });
      }
    }
  };

  return (
    <Flex
      bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
      px={{ base: '16px', md: '24px' }}
      py="24px"
      flexDir="column"
      mb="20px"
      onMouseEnter={() => setMouseHover(true)}
      onMouseLeave={() => setMouseHover(false)}
      boxShadow="0 1px 0 rgba(0,176,202,0.08), 0 4px 24px rgba(0,0,0,0.05)"
      w="100%"
      borderRadius="20px"
      border="2px solid"
      borderColor={{ base: 'rgba(0,176,202,0.1)', _dark: 'rgba(0,176,202,0.07)' }}
    >
      {title && (
        <Flex w="full" mb="16px" align="center" justify="space-between" gap="10px">
          <Flex align="center" gap="10px" minW="0">
            <Flex
              w="4px"
              h="26px"
              borderRadius="full"
              bg="#00B0CA"
              flexShrink={0}
            />
            <Icon color="#00B0CA" w="24px" h="24px" flexShrink={0}>
              <IconReact />
            </Icon>
            <Text
              fontSize="20px"
              fontWeight="800"
              color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
              letterSpacing="-0.3px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {title}
            </Text>
            {tagLabel && (
              <Flex
                px="10px"
                py="4px"
                borderRadius="full"
                bg={tagColor || '#DC3232'}
                flexShrink={0}
              >
                <Text fontSize="12px" fontWeight="700" color="#fff">
                  {tagLabel}
                </Text>
              </Flex>
            )}
          </Flex>
          {viewAllHref && (
            <Link href={viewAllHref} style={{ flexShrink: 0 }}>
              <Flex
                align="center"
                gap="4px"
                px="12px"
                py="6px"
                borderRadius="full"
                border="1.5px solid rgba(0,176,202,0.3)"
                _hover={{ bg: 'rgba(0,176,202,0.08)' }}
                transition="all 0.15s"
              >
                <Text fontSize="13px" fontWeight="600" color="#00B0CA">
                  Ver tudo
                </Text>
                <Icon color="#00B0CA" w="12px" h="12px">
                  <LuArrowRight />
                </Icon>
              </Flex>
            </Link>
          )}
        </Flex>
      )}
      {products.length === 0 ? (
        <Flex align="center" justify="center" minH="240px">
          <Text
            fontSize="14px"
            color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.25)' }}
          >
            Sem produtos disponíveis
          </Text>
        </Flex>
      ) : (
        <Flex pos="relative" align="center" justify="start">
          <Grid
            ref={gridRef}
            gridAutoFlow="column"
            gridAutoColumns="minmax(260px, 1fr)"
            minH="320px"
            gap="16px"
            overflow="hidden"
            overflowX="scroll"
            scrollbarWidth="none"
            css={{ '&::-webkit-scrollbar': { display: 'none' } }}
            px="4px"
            py="8px"
            alignContent="start"
          >
            {products.map(
              (product) =>
                product && <ProductCard key={product.id} product={product} />,
            )}
          </Grid>
          {showArrows && (
            <>
              <ArrowButton onClick={scrollLeft} direction="left" />
              <ArrowButton onClick={scrollRight} direction="right" />
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ProductsGrid;
